// src/helpers/milestones/milestone.js

import milestonesConfig from "@/helpers/arrays/milestones.js";
  import { useIdentityStore } from '@/stores/identity'


/**
 * Build a fast lookup map once.
 * Keyed by milestone "name" (e.g. "Migration")
 */
const milestoneMap = Object.fromEntries(
  (milestonesConfig ?? []).map((m) => [m.name, m])
);

/**
 * Lookup a milestone definition from the config array by name.
 * @param {string} name
 * @returns {object|null}
 */
export function getMilestoneDefinition(name) {
  if (!name) return null;
  return milestoneMap[name] ?? null;
}

/**
 * Whether the current user can see a milestone.
 *
 * Assumption:
 * - admin/intermediary users can see all milestones
 * - other users cannot see internal milestones
 * - other users also cannot see milestones not visible to intermediaries
 *
 * Adjust the identity.type values to match your actual app.
 *
 * @param {object} milestone
 * @param {object} identity
 * @returns {boolean}
 */
function canViewMilestone(milestone, identity) {
  if (!milestone) return false;

  const definition = getMilestoneDefinition(milestone.type);

  // If no config exists, allow it through rather than hiding unexpectedly
  if (!definition) return true;

  const isAdmin = identity?.type === "Administrator";
  const isIntermediary = identity?.type === "Intermediary";

  if (isAdmin || isIntermediary) {
    return true;
  }

  return !definition.isInternalMilestone && definition.visibleToIntermediary;
}

/**
 * Mimics the API logic for IsCurrent, but works it out locally.
 *
 * API logic being mirrored:
 * - ignore completed milestones
 * - order by scheduled completion
 * - first remaining milestone is the current one
 *
 * Also applies visibility rules based on milestone config + identity.
 *
 * @param {object} project
 * @param {object} identity
 * @returns {object|null}
 */
export function getCurrentMilestone(project, identity) {
  if (!project?.milestones?.length) return null;

  const visibleMilestones = project.milestones.filter((milestone) =>
    canViewMilestone(milestone, identity)
  );

  if (!visibleMilestones.length) return null;

  const currentMilestone = visibleMilestones
    .filter((milestone) => milestone.status !== "SuccessfullyCompleted")
    .sort((a, b) => {
      const aDate = a?.scheduledCompletion
        ? new Date(a.scheduledCompletion).getTime()
        : Number.MAX_SAFE_INTEGER;

      const bDate = b?.scheduledCompletion
        ? new Date(b.scheduledCompletion).getTime()
        : Number.MAX_SAFE_INTEGER;

      return aDate - bDate;
    })[0];

  if (!currentMilestone) return null;

  const definition = getMilestoneDefinition(currentMilestone.type);

  return {
    ...currentMilestone,
    definition: definition ?? null,
    displayName: definition?.displayName ?? currentMilestone.type,
    description: definition?.description ?? null,
    isInternalMilestone: definition?.isInternalMilestone ?? false,
    visibleToClient: definition?.visibleToClient ?? true,
    visibleToIntermediary: definition?.visibleToIntermediary ?? true
  };
}

// keep your existing function, but now use getCurrentMilestone
export async function getMilestoneProgress(project) {

  const identity = useIdentityStore();  


  if (!project?.milestones?.length) {
    return {
      totalMilestoneCount: 0,
      numberOfMilestonesComplete: 0,
      currentMilestoneName: null,
      currentMilestoneDueDate: null
    };
  }

  const visibleMilestones = project.milestones.filter((milestone) =>
    canViewMilestone(milestone, identity)
  );

  const totalMilestoneCount = visibleMilestones.length;

  const numberOfMilestonesComplete = visibleMilestones.filter(
    (m) => m.status === "SuccessfullyCompleted"
  ).length;

  const currentMilestone = getCurrentMilestone(project, identity);

  return {
    totalMilestoneCount,
    numberOfMilestonesComplete,
    currentMilestoneName:
      totalMilestoneCount === numberOfMilestonesComplete
        ? "Project Complete"
        : currentMilestone?.displayName ?? currentMilestone?.type ?? null,
    currentMilestoneDueDate: currentMilestone?.scheduledCompletion ?? null
  };
}